import { createContext, useCallback, useContext, useRef } from "react";
import type Backbone from "backbone";

export const BackboneStateContext = createContext<RootBackboneState>(null);

type RootBackboneStateTypes = "Collections" | "Models" | "Routers";
type RootBackboneState = Pick<AppInterface, RootBackboneStateTypes>;

import { useSyncExternalStore } from "use-sync-external-store/shim";

export const useSyncedBackboneObject = (
    backboneObj?: Backbone.Events,
    events = "all"
) => {
    const snapshot = useRef(false);
    const subscribe = useCallback(
        (callback) => {
            if (!backboneObj) {
                return;
            }
            backboneObj.on(events, () => {
                snapshot.current = !snapshot.current;
                callback();
            });

            return () => {
                if (!backboneObj) {
                    return;
                }
                backboneObj.off(events, callback);
            };
        },
        [backboneObj]
    );

    const getSnapshot = () => {
        return snapshot.current;
    };

    useSyncExternalStore(subscribe, getSnapshot);
    return backboneObj;
};

export const useBackboneAppObject = (
    selector: (backboneApp: RootBackboneState) => Backbone.Events
) => useSyncedBackboneObject(selector(useContext(BackboneStateContext)));

export const useBackboneUser = () =>
    useBackboneAppObject((app) => app.Models.user);

export const useBackboneGrab = (grabId: string) =>
    useSyncedBackboneObject(App?.Collections?.grabs.get(grabId));

export const useBackboneGrabs = () =>
    useSyncedBackboneObject(App?.Collections?.grabs);
